import '.f{width:100%;}.ssr-plugin'
import '.a7{position:relative;}.ssr-plugin'
import '.gh{height:36px;}.ssr-plugin'
import '.a1{gap:4px;}.ssr-plugin'
import '.em{display:inline-flex;}.ssr-plugin'
import '.c{justify-content:center;}.ssr-plugin'
import '.d{align-items:center;}.ssr-plugin'
import '.gi{padding:0 8px;}.ssr-plugin'
import '.gj{padding-right:0px;}.ssr-plugin'
import '.eq{outline:none;}.ssr-plugin'
import '.au{pointer-events:none;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.am{height:100%;}.ssr-plugin'
import '.i{background:transparent;}.ssr-plugin'
import '.j{border:none;}.ssr-plugin'
import '.fx{padding:0px;}.ssr-plugin'
import '.ac{flex-direction:column;}.ssr-plugin'
import '.gk{min-width:28px;}.ssr-plugin'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { radius } from '../../utils/radius.js'
import { Icon, IconProps } from '../Icon/index.js'
import { Text } from '../Text/index.js'
import { colors } from '../../tokens/colors.js'
import { TEXT_VARIANT_STYLES } from '../../tokens/typography.js'
import { LabelDescription } from '../LabelDescription/index.js'
import { Style, styled } from 'inlines'
import { mergeRefs } from '../../utils/mergeRefs.js'
import { Button } from '../Button/index.js'

export type NumberInputProps = {
  value?: number
  onChange?: (value: number) => void
  disabled?: boolean
  placeholder?: string
  prefix?: string
  leadIcon?: IconProps['variant']
  error?: boolean
  suffix?: string
  autoFocus?: boolean
  label?: string
  description?: string
  errorMessage?: string
  showSteps?: boolean
  step?: number
  min?: number
  max?: number
  int?: boolean
  style?: Style
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      value,
      onChange,
      placeholder,
      error,
      disabled,
      prefix,
      leadIcon,
      suffix,
      autoFocus,
      label,
      description,
      errorMessage,
      showSteps = true,
      step = 1,
      min,
      max,
      int = false,
      style,
    },
    externalRef,
  ) => {
    const [internalValue, setInternalValue] = useState(value)
    const [focus, setFocus] = useState(false)
    const innerRef = useRef<HTMLInputElement>(null)

    const actualValue = internalValue ?? value

    const applyStep = (
      value: number,
      step: number,
      operation: 'add' | 'sub',
    ) => {
      const decimals = step.toString().split('.')[1]?.length ?? 0
      const factor = Math.pow(10, decimals)

      const result =
        operation === 'add'
          ? Math.round((value + step) * factor) / factor
          : Math.round((value - step) * factor) / factor

      return result
    }

    useEffect(() => {
      setInternalValue(focus ? value : undefined)
    }, [focus])

    return (
      <LabelDescription
        label={label}
        description={description}
        error={error && errorMessage}
      >
        <styled.div className="f a7 gh a1 em c d gi gj eq"
          style={{
            borderRadius: radius[12],
            border: `1px solid ${colors.neutral20Adjusted}`,
            color: colors.neutral60,
            ...(focus &&
              !disabled && {
                background: colors.neutralInverted100,
                border: `1px solid ${colors.neutral100}`,
                outline: `4px solid ${colors.neutral20}`,
              }),
            ...(error && {
              border: `1px solid ${colors.red80}`,
              ...(focus &&
                !disabled && {
                  background: colors.neutralInverted100,
                  border: `1px solid ${colors.red100}`,
                  outline: `4px solid ${colors.red60}`,
                }),
            }),
            ...(disabled && {
              color: colors.neutral20,
              background: colors.neutral5,
              border: `1px solid transparent`,
            }),
            '& input::-webkit-outer-spin-button,input::-webkit-inner-spin-button':
              {
                WebkitAppearance: 'none',
                margin: 0,
              },
            '& input[type=number]': {
              MozAppearance: 'textfield',
            },
            ...style,
          }}
        >
          {leadIcon && (
            <span className="au a c d"
            >
              <Icon variant={leadIcon} />
            </span>
          )}
          {prefix && (
            <span className="au" style={{ marginRight: -4,}}>
              <Text color="inherit">{prefix}</Text>
            </span>
          )}
          <styled.input className="f am i eq j fx"
            ref={(node) => mergeRefs(node, innerRef, externalRef)}
            value={actualValue}
            disabled={disabled}
            min={min}
            max={max}
            placeholder={placeholder}
            autoFocus={autoFocus}
            type="number"
            inputMode="numeric"
            style={{
              color: disabled
                ? colors.neutral20
                : actualValue == undefined
                  ? colors.neutral60
                  : colors.neutral100,
              ...(disabled && {
                cursor: 'not-allowed',
              }),
              ...TEXT_VARIANT_STYLES['display-regular'],
            }}
            onKeyDown={(e) => {
              const allowedKeys = [
                'Backspace',
                'Delete',
                'Tab',
                'Escape',
                'Enter',
                'Home',
                'End',
                'ArrowLeft',
                'ArrowRight',
                'Clear',
                'Copy',
                'Paste',
              ]
              const isNumber = /^[0-9]$/.test(e.key)
              const isDecimalPoint = e.key === '.' || e.key === ','
              const isMinus = e.key === '-'

              if (
                !allowedKeys.includes(e.key) &&
                !isNumber &&
                !isDecimalPoint &&
                !isMinus
              ) {
                e.preventDefault()
                return
              }

              if (
                isMinus &&
                (e.currentTarget.value.includes('-') ||
                  e.currentTarget.selectionStart !== 0)
              ) {
                e.preventDefault()
                return
              }

              if (isDecimalPoint) {
                if (int || e.currentTarget.value.includes('.')) {
                  e.preventDefault()
                  return
                }
              }
            }}
            // onInput={(e) => {
            //   const target = e.target as HTMLInputElement
            //   const validPattern = int ? /^-?\d*$/ : /^-?\d*\.?\d*$/
            //   if (!validPattern.test(target.value)) {
            //     // @ts-ignore
            //     target.value = actualValue ?? ''
            //     e.preventDefault()
            //     return
            //   }
            // }}
            onChange={(e) => {
              let raw = e.target.value

              if (raw === '') {
                setInternalValue(undefined)
                onChange(undefined)
              } else {
                let parsed = int
                  ? Math.floor(Number.parseFloat(raw))
                  : Number.parseFloat(raw)
                if (Number.isNaN(parsed)) {
                  return
                }

                if (max !== undefined && parsed > max) {
                  parsed = max
                } else if (min !== undefined && parsed < min) {
                  parsed = min
                }

                setInternalValue(parsed)
                onChange(parsed)
              }
            }}
            onFocus={() => {
              setFocus(true)
            }}
            onBlur={() => {
              setFocus(false)
            }}
          />
          {suffix && (
            <span className="au"
              style={{
                marginLeft: -4,
                marginRight: disabled ? 8 : 4,
              }}
            >
              <Text color="inherit">{suffix}</Text>
            </span>
          )}
          {!disabled && showSteps && (
            <div className="a ac c d gk am"
              style={{
                background: colors.neutral10Adjusted,
                borderTopRightRadius: radius[12],
                borderBottomRightRadius: radius[12],}}
            >
              <Button
                tooltip={`+${step}`}
                size="tiny"
                leadIcon="tiny-chevron-up"
                variant="ghost"
                // keyHintTarget={innerRef.current ?? undefined}
                onClick={() => {
                  const currentValue = actualValue ?? 0
                  const newValue = applyStep(currentValue as any, step, 'add')

                  if (max === undefined || newValue <= max) {
                    setInternalValue(newValue)
                    onChange(newValue)
                  }
                }}
              />
              <Button
                size="tiny"
                leadIcon="tiny-chevron-down"
                tooltip={`-${step}`}
                variant="ghost"
                // keyHintTarget={innerRef.current ?? undefined}
                onClick={() => {
                  const currentValue = actualValue ?? 0
                  const newValue = applyStep(currentValue as any, step, 'sub')

                  if (min === undefined || newValue >= min) {
                    setInternalValue(newValue)
                    onChange(newValue)
                  }
                }}
              />
            </div>
          )}
        </styled.div>
      </LabelDescription>
    )
  },
)
